import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { TipoMoneda } from "@/constants/enums";
import { FutureContractDetail } from "@/pages/grain-requests/future-contract/schema/future-contract.schema";
import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import { Flex, GridItem, Spinner, Text } from "@chakra-ui/react";
import { Check, PenLine, X } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { Control, FieldValues, UseFormClearErrors } from "react-hook-form";

interface AmountCardProps {
  futureContract?: FutureContractDetail | null;
  isEditingAmount?: boolean;
  loadingEditAmount?: boolean;
  handleEditAmount?: () => void;
  errors?: any;
  control: Control<FieldValues>;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  clearErrors: UseFormClearErrors<FieldValues>;
  setValue: (name: string, value: any) => void;
  cantOperateFutureContract: boolean;
  hasWriteAccess: boolean;
}

export const AmountCard = ({
  futureContract,
  isEditingAmount,
  loadingEditAmount,
  handleEditAmount,
  errors,
  control,
  setIsEditingAmount,
  clearErrors,
  setValue,
  cantOperateFutureContract,
  hasWriteAccess,
}: AmountCardProps) => {

  const getTipoMoneda = () => {

    if (futureContract?.tipoMoneda === TipoMoneda.ARS && !futureContract?.sinBase) return "$"
    if (futureContract?.tipoMoneda === TipoMoneda.USD) return "U$D"
    return TipoMoneda[futureContract?.tipoMoneda as keyof typeof TipoMoneda];
  };
  return (
    <GridItem minW={isEditingAmount ? "240px" : "auto"}>
      <Flex
        minH={"82px"}
        gap="10px"
        padding="12px"
        flexDir="column"
        borderRadius="12px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Text
          fontWeight={700}
          textTransform="uppercase"
          fontSize="12px"
          color={AppColors.GRAY_TEXT}
        >
          Precio
        </Text>
        <Flex alignItems="center" justifyContent="space-between" gap="6px">
          {isEditingAmount ? (
            <InputCurrency
              control={control}
              name="future_contract_amount"
              defaultValue={String(futureContract?.montoSolicitado)}
              data-testid="future-contract-amount-input"
              maxLength={20}
              maxW="130px"
              hasError={Boolean(errors?.future_contract_amount)}
            />
          ) : (
            <Flex alignItems="center" gap="8px">
              <Text
                fontSize={{
                  base: "15px",
                  sm: "15px",
                  md: "15px",
                  lg: "18px",
                  xl: "20px",
                }}
                fontWeight={700}
                cursor={
                  futureContract?.sinBase || !hasWriteAccess
                    ? "default"
                    : "pointer"
                }
              >
                {futureContract?.sinBase
                  ? `${getTipoMoneda()} Sin base`
                  : `${getTipoMoneda()} ${formatCurrency(futureContract?.montoSolicitado)}`}
              </Text>

              {!cantOperateFutureContract && hasWriteAccess && (
                <PenLine
                  data-testid="cash-pen-line"
                  cursor="pointer"
                  onClick={() => {
                    setIsEditingAmount(true);
                    setValue(
                      "future_contract_amount",
                      String(futureContract?.montoSolicitado),
                    );
                  }}
                />
              )}
            </Flex>
          )}
          <Flex gap="8px">
            {loadingEditAmount && isEditingAmount && (
              <Spinner data-testid="cash-future-contract-edit-amount-spinner" />
            )}
            {isEditingAmount && !loadingEditAmount && (
              <Check
                cursor={
                  errors?.future_contract_amount ? "not-allowed" : "pointer"
                }
                onClick={handleEditAmount}
                data-testid="future-contract-edit-amount-button"
                size={18}
              />
            )}
            {isEditingAmount && (
              <X
                data-testid="future-contract-cancel-edit-button"
                cursor="pointer"
                onClick={() => {
                  setIsEditingAmount(false);
                  clearErrors("future_contract_amount");
                }}
                size={18}
              />
            )}
          </Flex>
        </Flex>
        {errors?.future_contract_amount && (
          <Text color={AppColors.ERROR_TEXT}>
            {errors?.future_contract_amount?.message as string}
          </Text>
        )}
      </Flex>
    </GridItem>
  );
};
